import * as React from "react";
import {TaskStatusDetails} from "areas/projects/components/ProjectDashboard/TaskStatusDetails/TaskStatusDetails";
import {DashboardItemResource, TaskState} from "client/resources";
import {DataCube} from "../../DashboardDataSource/DataCube";
import {NavigationButton, ActionButton} from "components/Button";
import TenantDeploymentCount from "../TenantDeploymentCount/index";
import ToolTip from "components/ToolTip/index";
import * as cn from "classnames";
import moment = require("moment");
import styles = require("./style.less");
import routeLinks from "../../../../../routeLinks";
import {DeploymentCreateGoal} from "../../Releases/ReleasesRoutes/releaseRouteLinks";
import PermissionCheck from "components/PermissionCheck/PermissionCheck";
import Permission from "client/resources/permission";

interface DeployUnavailableWarningProps {
    deployments: DashboardItemResource[];
    deploymentContext: DeploymentContext;
    cube: DataCube;
    allowDeployments: boolean;
    showDeploymentCounts: boolean;
}

type DeploymentContext = {environment: string, release: string, tenant: string, project: string};
type DeploymentContextIndex = {[index: string]: DashboardItemResource};

class DashboardCell extends React.PureComponent<DeployUnavailableWarningProps> {

    render() {
        const {deployments, deploymentContext, cube, allowDeployments} = this.props;
        if (deployments && deployments.length) {
            return this.renderTaskStatusCell();
        }

        if (!allowDeployments) {
            return null;
        }

        const projectId = deploymentContext.project;
        const releaseId = deploymentContext.release;
        const environmentId = deploymentContext.environment;
        const tenantId = deploymentContext.tenant;

         // You've got a tenant... but this tenant can't deploy this environment so... nah
        if (!!tenantId && cube.tenantIndex[tenantId].ProjectEnvironments[projectId].indexOf(environmentId) === -1) {
            const tenant = cube.tenantIndex[tenantId];
            const environment = cube.environmentIndex[environmentId];
            return <div className={styles.centerCell}>
                {this.renderNoDeployCell(`${tenant.Name} is not linked to the ${environment.Name} environment`, projectId, environmentId, tenantId)}
            </div>;
        }

        // We are looking at an untenanted deployment on a project that can't do untenanted deployments, so... nope.
        if (!tenantId && !cube.projectIndex[projectId].CanPerformUntenantedDeployment) {
            return <div className={styles.centerCell}>
                {this.renderNoDeployCell(`This project can not perform untenanted deployments`, projectId, environmentId)}
            </div>;
        }

        // No release
        if (!releaseId) {
            let text = "Create a release first to deploy project";
            if (Object.keys(cube.releaseIndex).length > 0) { // has releases
                text = "Filter the dashboard by releases to perform a deployment";
            }

            return <div className={styles.centerCell}>
                <ToolTip content={text}>
                    <ActionButton label="Deploy" disabled={true}/>
                </ToolTip>
            </div>;
        }

        // This scenario is likely only available when we add more filters
        if (!environmentId || !projectId) {
            return <div className={styles.centerCell}>
                {this.getUnavailableToDeployWarning(projectId, environmentId)}
            </div>;
        }

        // Ok... so looks like you can deploy to this environment
        const release = cube.releaseIndex[releaseId];
        const environmentReleases = cube.nextAvailableDeployments[releaseId];
        if (environmentReleases) {
            const tenantReleases = environmentReleases[environmentId];
            if (tenantReleases && tenantReleases.indexOf(tenantId) !== -1) { // don't worry. when untenanted tenantId=null
                const uri = routeLinks.project(cube.projectIndex[projectId])
                    .release(release.Version)
                    .deployments.create(DeploymentCreateGoal.To, environmentId, tenantId);
                const tooltipTitle = this.getTooltipTitleForCube(cube);
                return <div className={styles.centerCell}>
                    <PermissionCheck
                        permission={Permission.DeploymentCreate}
                        project={projectId}
                        environment={environmentId}
                        tenant={tenantId}>
                        <NavigationButton label="Deploy" href={uri} tooltipTitle={tooltipTitle} />
                    </PermissionCheck>
                </div>;
            }
        }

        return <div className={styles.centerCell}>
            {this.renderNoDeployCell(
                `Release ${release.Version} can not be deployed to the ${cube.environmentIndex[environmentId].Name} environment`,
                projectId,
                environmentId,
                tenantId)}
        </div>;
    }

    private renderTaskStatusCell() {
        const {cube, showDeploymentCounts} = this.props;
        const visibleDeploymentItem = this.props.deployments
            .find((item) => item.State === TaskState.Executing ||
                item.State === TaskState.Failed ||
                (item.State === TaskState.Success && item.HasWarningsOrErrors)) || this.props.deployments[0];

        const totalDiv = showDeploymentCounts &&
            this.tenantDeploymentCount(visibleDeploymentItem);
        const channel = Object.keys(cube.projectIndex).length === 1 && Object.keys(cube.channelIndex).length > 1 ?
            cube.channelIndex[visibleDeploymentItem.ChannelId].Name :
            undefined;
        const project = cube.projectIndex[this.props.deploymentContext.project];
        const projectSlug = project ? project.Slug : null;
        const tooltipTitle = this.getTooltipTitleForCube(cube);
        return <div className={styles.taskDetailsCell}>
            <TaskStatusDetails item={visibleDeploymentItem}
                channelName={channel}
                projectSlug={projectSlug}
                deploymentId={visibleDeploymentItem.DeploymentId}
                additionalDetails={totalDiv}
                tooltipTitle={tooltipTitle}
            />
        </div>;
    }

    private renderNoDeployCell(message: string, projectId: string, environmentId: string, tenantId?: string) {
        return <PermissionCheck
            permission={Permission.DeploymentCreate}
            project={projectId}
            environment={environmentId}
            tenant={tenantId}>
            <ToolTip
                content={message}>
                <div className={styles.blockIcon}>
                    <i className={cn("fa fa-ban")}/>
                </div>
            </ToolTip>
        </PermissionCheck>;
    }

    // This is for customers operating at scale. We couldn't find a way to get floating first header/columns working, so this
    // is the cheap solution :) But it gives them the basic information they need to see what a dashboard cube is referring to
    // just by hovering over the cube.
    private getTooltipTitleForCube(cube: DataCube): string {
        const environmentId = this.props.deploymentContext.environment;
        const releaseId = this.props.deploymentContext.release;
        const tenantId = this.props.deploymentContext.tenant;
        const projectId = this.props.deploymentContext.project;

        let tooltipTitle = null;
        if (environmentId) {
            const environment = cube.environmentIndex[environmentId];
            if (tenantId) {
                const tenant = cube.tenantIndex[tenantId];
                tooltipTitle = `${tenant.Name} | ${environment.Name}`;
            } else if (releaseId) {
                const release = cube.releaseIndex[releaseId];
                tooltipTitle = `Release ${release.Version} | ${environment.Name}`;
            } else if (projectId) {
                const project = cube.projectIndex[projectId];
                tooltipTitle = `${project.Name} | ${environment.Name}`;
            }
        }
        return tooltipTitle;
    }

    private getUnavailableToDeployWarning = (projectId: string, environmentId: string) => {
        const missing: string[] = [];

        if (!projectId) {
            missing.push("project");
        }

        if (!environmentId) {
            missing.push("environment");
        }

        let missingDescription: string = "";
        if (missing.length === 1) {
            missingDescription = missing[0];
        } else if (missing.length > 1) {
            missingDescription = missing.slice(0, missing.length - 1).join(", ").concat(" and ").concat(missing[missing.length - 1]);
        }

        const text = `Filter the dashboard by ${missingDescription} to perform a deployment.`;
        return (<ToolTip content={text}>
            <ActionButton label="Deploy" disabled={true}/>
        </ToolTip>);
    }

    private tenantDeploymentCount = (visibleDeploymentItem: DashboardItemResource): any => {
        const {deployments, deploymentContext, cube} = this.props;
        if (deploymentContext.tenant) {
            // If tenant already in context, then no point extending in "Z" dimension
            return null;
        }

        const projectId = deploymentContext.project;
        if (!projectId) {
            // Seems like tenants aren't involved in this project
            return null;
        }
        const environmentId = deploymentContext.environment;
        if (!environmentId) {
            // We need an environment to tell how many tenants are relevant
            return null;
        }

        const tenantsInProjectEnvironment = Object.keys(cube.tenantIndex)
            .filter(tid => {
                const tenant = cube.tenantIndex[tid];
                const projectEnvs =  tenant.ProjectEnvironments[projectId];
                return projectEnvs && projectEnvs.indexOf(environmentId) !== -1;
            }).map(t => cube.tenantIndex[t]);

        if (tenantsInProjectEnvironment.length === 0) {
            // No tenants involved in this tenanted project, no point showing anything
            return null;
        }

        const haveCountedTenant: any[] = [];
        const progress = deployments.reduce((sum, d: any) => {
            if (d.ReleaseId === visibleDeploymentItem.ReleaseId && //Only show count of "current visible" release on grid
                d.TenantId && !haveCountedTenant[d.TenantId] && // Already considered latest deployment for this tenant
                tenantsInProjectEnvironment.find(t => t.Id === d.TenantId)) { //Only consider if tenant currently in environment (ignore if deployed when previously linked)
                haveCountedTenant[d.TenantId] = true;
                if (d.State === TaskState.Success) {
                    return sum + 1;
                }
            }
            return sum;
        }, 0);

        return <TenantDeploymentCount progress={progress} expected={tenantsInProjectEnvironment.length}/>;
    }
}

export default DashboardCell;


// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--1!./app/areas/projects/components/ProjectDashboard/DashboardCell/DashboardCell.tsx